import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Award, TrendingUp } from "lucide-react";

const Leaderboard = () => {
  const { token, user } = useContext(AuthContext);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeaderboard(response.data);
    } catch (error) {
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-8 h-8 text-yellow-500" />;
      case 2:
        return <Medal className="w-8 h-8 text-gray-400" />;
      case 3:
        return <Medal className="w-8 h-8 text-orange-600" />;
      default:
        return <span className="text-2xl font-bold text-gray-400">#{rank}</span>;
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1:
        return "from-yellow-100 to-yellow-50 border-yellow-300";
      case 2:
        return "from-gray-100 to-gray-50 border-gray-300";
      case 3:
        return "from-orange-100 to-orange-50 border-orange-300";
      default:
        return "from-white to-gray-50 border-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="leaderboard-container">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Trophy className="w-10 h-10 text-yellow-500" />
            Leaderboard
          </h1>
          <p className="text-gray-600 text-lg">Top performers in career exploration</p>
        </div>

        {/* Current User Stats */}
        <Card className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600">
                  <AvatarFallback className="text-white text-xl font-bold">
                    {user?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-xl" data-testid="user-leaderboard-name">{user?.name}</h3>
                  <p className="text-gray-600">Grade {user?.grade}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end mb-1">
                  <TrendingUp className="w-5 h-5 text-indigo-500" />
                  <span className="text-3xl font-bold" data-testid="user-leaderboard-points">{user?.points || 0}</span>
                </div>
                <p className="text-sm text-gray-600">Total Points</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top 10 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-6 h-6" />
              Top 10 Students
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {leaderboard.length === 0 ? (
              <div className="text-center py-12" data-testid="no-leaderboard-data">
                <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No data yet. Be the first to climb the leaderboard!</p>
              </div>
            ) : (
              leaderboard.map((student, index) => {
                const rank = index + 1;
                const isCurrentUser = student.id === user?.id;
                return (
                  <div
                    key={student.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 bg-gradient-to-r ${
                      getRankColor(rank)
                    } ${isCurrentUser ? 'ring-2 ring-indigo-500' : ''}`}
                    data-testid={`leaderboard-rank-${rank}`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 flex justify-center">{getRankIcon(rank)}</div>
                      <Avatar className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600">
                        <AvatarFallback className="text-white font-bold">
                          {student.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h4 className="font-semibold">
                          {student.name}
                          {isCurrentUser && <span className="text-indigo-600 ml-2">(You)</span>}
                        </h4>
                        <p className="text-sm text-gray-600">Grade {student.grade}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-indigo-600">{student.points}</div>
                      <p className="text-xs text-gray-500">points</p>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* How to Earn Points */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>How to Earn Points</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <Target className="w-5 h-5 text-blue-500 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Complete Assessments</h4>
                  <p className="text-sm text-gray-600">+50 points per assessment</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <MessageSquare className="w-5 h-5 text-green-500 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Use AI Chatbot</h4>
                  <p className="text-sm text-gray-600">+10 points per session</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <BookOpen className="w-5 h-5 text-purple-500 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Explore Careers</h4>
                  <p className="text-sm text-gray-600">+5 points per career viewed</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-500 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Book Consultations</h4>
                  <p className="text-sm text-gray-600">+25 points per booking</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

import { Target, BookOpen, MessageSquare, Calendar } from "lucide-react";

export default Leaderboard;